import { createColumnHelper } from '@tanstack/react-table';

import { PortainerSelect, Option } from '@@/form-components/PortainerSelect';

import type { UnifiedAccess } from './types';

const columnHelper = createColumnHelper<UnifiedAccess>();

interface GetColumnsDeps {
  accessList: UnifiedAccess[];
  onChange: (key: 'accessList', value: UnifiedAccess[]) => void;
  roleOptions: Option<number>[];
}

export function getColumns({
  accessList,
  onChange,
  roleOptions,
}: GetColumnsDeps) {
  return [
    columnHelper.accessor('Name', {
      header: 'Name',
      cell: ({ row }) => row.original.Name,
      meta: { width: '15%', className: 'overflow-hidden' },
    }),
    columnHelper.accessor('Type', {
      header: 'Type',
      cell: ({ getValue }) => {
        const type = getValue<string>();
        return type.charAt(0).toUpperCase() + type.slice(1);
      },
      meta: { width: '15%', className: 'overflow-hidden' },
    }),
    columnHelper.accessor('Role', {
      header: 'Role',
      id: 'role',
      cell: ({ row }) => (
        <RoleCell
          item={row.original}
          roleOptions={roleOptions}
          onChangeRole={handleRoleChange}
        />
      ),
      meta: { width: '70%', className: 'overflow-hidden' },
    }),
  ];

  function updateAccess(
    item: UnifiedAccess,
    updater: (prev: UnifiedAccess) => UnifiedAccess
  ) {
    const newAccessList = [...accessList];
    const index = newAccessList.findIndex(
      (access) => access.Type === item.Type && access.Id === item.Id
    );
    if (index !== -1) {
      newAccessList[index] = updater(newAccessList[index]);
      onChange('accessList', newAccessList);
    }
  }

  function handleRoleChange(item: UnifiedAccess, newRoleId: number) {
    const roleName =
      roleOptions.find((r) => r.value === newRoleId)?.label || '';

    updateAccess(item, (prev) => ({
      ...prev,
      Role: { Id: newRoleId, Name: roleName },
    }));
  }
}

function RoleCell({
  item,
  roleOptions,
  onChangeRole,
}: {
  item: UnifiedAccess;
  roleOptions: Option<number>[];
  onChangeRole: (item: UnifiedAccess, newRoleId: number) => void;
}) {
  return (
    <span className="min-w-max">
      <PortainerSelect<number>
        value={item.Role.Id}
        onChange={(value) => onChangeRole(item, value || 0)}
        options={roleOptions}
        placeholder="Select a role"
        bindToBody
        data-cy={`role-select-${item.Type}-${item.Id}`}
      />
    </span>
  );
}
